// src/pages/finance-portal/transactions/Transactions.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Avatar,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    InputAdornment,
    Switch,
    FormControlLabel,
    Autocomplete,
    Badge,
    Skeleton,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SyncIcon from '@mui/icons-material/Sync';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import PendingIcon from '@mui/icons-material/Pending';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const CATEGORY_COLORS = {
    'FOOD_AND_DRINK': '#f97316',
    'TRANSPORTATION': '#3b82f6',
    'TRAVEL': '#8b5cf6',
    'TRANSFER': '#64748b',
    'PAYMENT': '#06b6d4',
    'SHOPPING': '#ec4899',
    'ENTERTAINMENT': '#f59e0b',
    'PERSONAL_CARE': '#14b8a6',
    'GENERAL_SERVICES': '#6366f1',
    'GENERAL_MERCHANDISE': '#84cc16',
    'RENT_AND_UTILITIES': '#ef4444',
    'MEDICAL': '#dc2626',
    'BANK_FEES': '#94a3b8',
    'INCOME': '#22c55e',
    'LOAN_PAYMENTS': '#b91c1c',
    'HOME_IMPROVEMENT': '#a855f7',
    'GOVERNMENT_AND_NON_PROFIT': '#0ea5e9',
};

function formatCurrency(amount, currency = 'USD') {
    if (amount == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
    }).format(amount);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

export default function Transactions() {
    const { enqueueSnackbar } = useSnackbar();

    const [transactions, setTransactions] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    // Filters
    const [accountFilter, setAccountFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [search, setSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [pendingOnly, setPendingOnly] = useState(false);
    const [amountMin, setAmountMin] = useState('');
    const [amountMax, setAmountMax] = useState('');

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Detail dialog
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedTx, setSelectedTx] = useState(null);
    const [editGlAccountId, setEditGlAccountId] = useState(null);
    const [savingGl, setSavingGl] = useState(false);

    const fetchAccounts = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/finance/plaid/accounts');
            if (response.data.success) {
                setAccounts(response.data.data || []);
            }
        } catch (err) {
            // silent
        }
    }, []);

    const fetchGlAccounts = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/finance/coa?flat=true');
            if (response.data.success) {
                setGlAccounts(response.data.data || []);
            }
        } catch (err) {
            // silent
        }
    }, []);

    const fetchTransactions = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = {
                page: page + 1,
                per_page: rowsPerPage,
            };
            if (accountFilter) params.account_id = accountFilter;
            if (categoryFilter) params.category_primary = categoryFilter;
            if (search) params.search = search;
            if (dateFrom) params.from_date = dateFrom;
            if (dateTo) params.to_date = dateTo;
            if (pendingOnly) params.pending = true;
            if (amountMin) params.amount_min = amountMin;
            if (amountMax) params.amount_max = amountMax;

            const response = await axiosInstance.get('/api/finance/plaid/transactions', { params });
            if (response.data.success) {
                setTransactions(response.data.data || []);
                setTotalCount(response.data.total || 0);
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to load transactions', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, accountFilter, categoryFilter, search, dateFrom, dateTo, pendingOnly, amountMin, amountMax, enqueueSnackbar]);

    useEffect(() => {
        fetchAccounts();
        fetchGlAccounts();
    }, [fetchAccounts, fetchGlAccounts]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    const handleSyncAll = async () => {
        setSyncing(true);
        try {
            const response = await axiosInstance.post('/api/finance/plaid/transactions/sync-all');
            if (response.data.success) {
                enqueueSnackbar(`Synced ${response.data.synced || 0} transactions`, { variant: 'success' });
                fetchTransactions();
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Sync failed', { variant: 'error' });
        } finally {
            setSyncing(false);
        }
    };

    const handleRowClick = (tx) => {
        setSelectedTx(tx);
        setEditGlAccountId(tx.gl_account_id || null);
        setDetailOpen(true);
    };

    const handleSaveGlMapping = async () => {
        if (!selectedTx) return;
        setSavingGl(true);
        try {
            const response = await axiosInstance.put(`/api/finance/plaid/transactions/${selectedTx.id}`, {
                gl_account_id: editGlAccountId,
            });
            if (response.data.success) {
                enqueueSnackbar('GL account mapping saved', { variant: 'success' });
                setTransactions(prev =>
                    prev.map(t => t.id === selectedTx.id ? { ...t, gl_account_id: editGlAccountId } : t)
                );
                setDetailOpen(false);
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to save mapping', { variant: 'error' });
        } finally {
            setSavingGl(false);
        }
    };

    const categories = [...new Set(transactions.map(t => t.category_primary).filter(Boolean))].sort();

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ReceiptIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Transactions
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                View and manage bank transactions
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                        onClick={handleSyncAll}
                        disabled={syncing}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px',
                            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        {syncing ? 'Syncing...' : 'Sync All'}
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Filters */}
                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 2.5, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <FilterListIcon sx={{ fontSize: 18, color: '#64748b' }} />
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#64748b' }}>Filters</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel sx={{ fontFamily }}>Account</InputLabel>
                            <Select
                                value={accountFilter}
                                label="Account"
                                onChange={(e) => { setAccountFilter(e.target.value); setPage(0); }}
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                <MenuItem value="">All Accounts</MenuItem>
                                {accounts.map(a => (
                                    <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>
                                        {a.name} {a.mask ? `****${a.mask}` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            size="small"
                            type="date"
                            label="From Date"
                            value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                        />
                        <TextField
                            size="small"
                            type="date"
                            label="To Date"
                            value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                        />

                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel sx={{ fontFamily }}>Category</InputLabel>
                            <Select
                                value={categoryFilter}
                                label="Category"
                                onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {categories.map(c => (
                                    <MenuItem key={c} value={c} sx={{ fontFamily }}>{c.replace(/_/g, ' ')}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            size="small"
                            placeholder="Search merchant or name..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ minWidth: 200, fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                        />

                        <TextField
                            size="small"
                            type="number"
                            label="Min Amount"
                            value={amountMin}
                            onChange={(e) => { setAmountMin(e.target.value); setPage(0); }}
                            sx={{ width: 120, fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                        />
                        <TextField
                            size="small"
                            type="number"
                            label="Max Amount"
                            value={amountMax}
                            onChange={(e) => { setAmountMax(e.target.value); setPage(0); }}
                            sx={{ width: 120, fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={pendingOnly}
                                    onChange={(e) => { setPendingOnly(e.target.checked); setPage(0); }}
                                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                                />
                            }
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Pending Only</Typography>}
                        />
                    </Box>
                </Paper>

                {/* Table */}
                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Date', 'Merchant / Name', 'Amount', 'Category', 'Account', 'Status', 'GL Account'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [...Array(8)].map((_, i) => (
                                        <TableRow key={i}>
                                            {[...Array(7)].map((_, j) => (
                                                <TableCell key={j}><Skeleton variant="text" sx={{ fontFamily }} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : transactions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                            <ReceiptIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No transactions found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    transactions.map((tx) => {
                                        const catColor = CATEGORY_COLORS[tx.category_primary] || '#64748b';
                                        const amountVal = tx.amount || 0;
                                        const accountName = accounts.find(a => a.id === tx.account_id)?.name || tx.account_name || '—';
                                        const glAccount = glAccounts.find(g => g.id === tx.gl_account_id);

                                        return (
                                            <TableRow
                                                key={tx.id}
                                                hover
                                                onClick={() => handleRowClick(tx)}
                                                sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0fdf4' } }}
                                            >
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>
                                                    {formatDate(tx.date || tx.authorized_date)}
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        {tx.logo_url ? (
                                                            <Avatar src={tx.logo_url} sx={{ width: 28, height: 28, borderRadius: '8px' }} />
                                                        ) : (
                                                            <Avatar sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: `${catColor}15`, color: catColor, fontSize: '0.7rem', fontFamily, fontWeight: 700 }}>
                                                                {(tx.merchant_name || tx.name || '?')[0]}
                                                            </Avatar>
                                                        )}
                                                        <Box>
                                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                                {tx.merchant_name || tx.name || 'Unknown'}
                                                            </Typography>
                                                            {tx.merchant_name && tx.name && tx.merchant_name !== tx.name && (
                                                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{tx.name}</Typography>
                                                            )}
                                                        </Box>
                                                        {tx.pending && (
                                                            <Badge>
                                                                <Chip
                                                                    icon={<PendingIcon sx={{ fontSize: 12 }} />}
                                                                    label="Pending"
                                                                    size="small"
                                                                    sx={{ fontFamily, fontSize: '0.65rem', height: 20, bgcolor: '#fef3c7', color: '#92400e', '& .MuiChip-icon': { color: '#92400e' } }}
                                                                />
                                                            </Badge>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: amountVal < 0 ? '#ef4444' : ACCENT, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>
                                                    {formatCurrency(amountVal, tx.iso_currency_code)}
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    {tx.category_primary && (
                                                        <Chip
                                                            label={tx.category_primary.replace(/_/g, ' ')}
                                                            size="small"
                                                            sx={{
                                                                fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 24,
                                                                bgcolor: `${catColor}15`, color: catColor,
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                                                    {accountName}
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <Chip
                                                        label={tx.pending ? 'Pending' : 'Posted'}
                                                        size="small"
                                                        sx={{
                                                            fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                            bgcolor: tx.pending ? '#fef3c7' : '#dcfce7',
                                                            color: tx.pending ? '#92400e' : '#166534',
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                                                    {glAccount ? `${glAccount.account_number} - ${glAccount.name}` : '—'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{ fontFamily, borderTop: '1px solid #e2e8f0' }}
                    />
                </Paper>
            </Box>

            {/* Detail Dialog */}
            <Dialog
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    Transaction Detail
                </DialogTitle>
                {selectedTx && (
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                {selectedTx.logo_url ? (
                                    <Avatar src={selectedTx.logo_url} sx={{ width: 40, height: 40, borderRadius: '10px' }} />
                                ) : (
                                    <Avatar sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: `${ACCENT}15`, color: ACCENT, fontFamily, fontWeight: 700 }}>
                                        {(selectedTx.merchant_name || selectedTx.name || '?')[0]}
                                    </Avatar>
                                )}
                                <Box>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                        {selectedTx.merchant_name || selectedTx.name || 'Unknown'}
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>
                                        {formatDate(selectedTx.date || selectedTx.authorized_date)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography sx={{
                                fontFamily, fontWeight: 800, fontSize: '1.6rem',
                                color: (selectedTx.amount || 0) < 0 ? '#ef4444' : ACCENT,
                            }}>
                                {formatCurrency(selectedTx.amount, selectedTx.iso_currency_code)}
                            </Typography>

                            {selectedTx.pending && (
                                <Chip
                                    icon={<PendingIcon sx={{ fontSize: 14 }} />}
                                    label="Pending Transaction"
                                    sx={{ fontFamily, bgcolor: '#fef3c7', color: '#92400e', alignSelf: 'flex-start', '& .MuiChip-icon': { color: '#92400e' } }}
                                />
                            )}

                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>Primary Category</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', fontWeight: 500 }}>
                                        {selectedTx.category_primary?.replace(/_/g, ' ') || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>Detailed Category</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', fontWeight: 500 }}>
                                        {selectedTx.category_detailed?.replace(/_/g, ' ') || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>Payment Channel</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', fontWeight: 500 }}>
                                        {selectedTx.payment_channel || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>Transaction ID</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', fontFamily: 'monospace' }}>
                                        {selectedTx.plaid_transaction_id || selectedTx.id || '—'}
                                    </Typography>
                                </Box>
                            </Box>

                            {(selectedTx.location_city || selectedTx.location_region || selectedTx.location_address) && (
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>Location</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b' }}>
                                        {[selectedTx.location_address, selectedTx.location_city, selectedTx.location_region].filter(Boolean).join(', ')}
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ mt: 1 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>GL Account Mapping</Typography>
                                <Autocomplete
                                    options={glAccounts}
                                    getOptionLabel={(opt) => opt ? `${opt.account_number} - ${opt.name}` : ''}
                                    value={glAccounts.find(g => g.id === editGlAccountId) || null}
                                    onChange={(_, val) => setEditGlAccountId(val?.id || null)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder="Select GL Account..."
                                            sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                                        />
                                    )}
                                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                )}
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDetailOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveGlMapping}
                        disabled={savingGl}
                        startIcon={savingGl ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                        }}
                    >
                        {savingGl ? 'Saving...' : 'Save Mapping'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
